import React, { useState, useEffect } from 'react';
import { AuthPanel } from '../components/AuthPanel';
import { AdminPanelModal } from '../components/AdminPanelModal';
import { useAuth } from '../auth/useAuth';
import { DemoHandTrainer } from '../components/demo';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PricingSection } from '../components/billing/PricingSection';
import { BILLING_ENABLED } from '../config/plans';

type Props = {
  onEnterApp: () => void;
  onViewPartners: () => void;
};

type InquiryType = 'free-trial' | 'partnership' | 'pricing' | 'custom';

// Lets any CTA preselect the contact form's inquiry type and scroll to it.
const CONTACT_EVENT = 'bjt:contact-inquiry';
function openContactInquiry(inquiry: InquiryType) {
  window.dispatchEvent(new CustomEvent(CONTACT_EVENT, { detail: inquiry }));
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
}

// Hero features - the main selling points
const heroFeatures = [
  {
    icon: '🃏',
    title: 'Hand Trainer',
    subtitle: 'with Strategy Feedback',
    description: 'Practice every decision with instant feedback. Get corrected on basic strategy mistakes and learn the correct play for every situation.',
  },
  {
    icon: '🧠',
    title: '5 Counting Systems',
    subtitle: 'Hi-Lo, Hi-Opt I & II, Omega II, KO',
    description: 'Switch systems instantly. Running count and true count tracked automatically with configurable deck penetration.',
  },
  {
    icon: '🎯',
    title: 'Illustrious 18',
    subtitle: 'Index Play Deviations',
    description: 'Master the plays that separate pros from amateurs. Get alerts when a deviation applies and feedback on whether you played it correctly.',
  },
];

// Feature categories
const featureCategories = [
  {
    title: 'Training Modes',
    items: [
      { icon: '🎰', name: 'Multi-Seat Simulator', desc: '1-7 seats with AI opponents' },
      { icon: '⚡', name: 'Speed Drills', desc: 'Count cards at casino pace' },
      { icon: '📋', name: 'Practice Scenarios', desc: 'Drill specific situations' },
      { icon: '❓', name: 'Count Checks', desc: 'Random quizzes to test focus' },
    ],
  },
  {
    title: 'Analytics & Tools',
    items: [
      { icon: '📊', name: 'Bet Optimization', desc: 'Optimal sizing for any count' },
      { icon: '📈', name: 'Edge Calculators', desc: 'Model any ruleset' },
      { icon: '🏆', name: 'XP & Achievements', desc: 'Track your progress' },
      { icon: '📱', name: 'Mobile-First', desc: 'Swipe controls, any device' },
    ],
  },
];

const inquiryOptions: { value: InquiryType; label: string }[] = [
  { value: 'free-trial', label: 'Request a Free Trial' },
  { value: 'partnership', label: 'Potential Partnership' },
  { value: 'pricing', label: 'Pricing Question' },
  { value: 'custom', label: 'Custom / Profit-Sharing' },
];

// Contact Form Component
const ContactForm: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [inquiryType, setInquiryType] = useState<InquiryType | ''>('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // Allow CTAs elsewhere on the page (e.g. "Custom & Partnerships") to preselect
  // the inquiry type when they scroll the user down to this form.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as InquiryType | undefined;
      if (detail) setInquiryType(detail);
    };
    window.addEventListener(CONTACT_EVENT, handler);
    return () => window.removeEventListener(CONTACT_EVENT, handler);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const functions = getFunctions();
      const sendContactForm = httpsCallable(functions, 'sendContactForm');
      await sendContactForm({ name, email, inquiryType, message });
      setStatus('success');
      setName('');
      setEmail('');
      setInquiryType('');
      setMessage('');
    } catch (err: unknown) {
      setStatus('error');
      const error = err as { message?: string };
      setErrorMessage(error.message || 'Failed to send message. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="p-8 rounded-2xl bg-gradient-to-br from-green-500/20 to-green-600/10 border border-green-500/30 text-center">
        <div className="text-4xl mb-4">✓</div>
        <h3 className="font-cinzel text-2xl font-bold text-white mb-2">Message Sent!</h3>
        <p className="text-white/70">Thank you for reaching out. We'll get back to you soon.</p>
        <button
          onClick={() => setStatus('idle')}
          className="mt-6 px-6 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white font-medium transition-all"
        >
          Send Another Message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-white/[0.08] to-white/[0.02] border border-white/10">
      <div className="space-y-5">
        {/* Name */}
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-white/80 mb-2">
            Name
          </label>
          <input
            type="text"
            id="contact-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all"
            placeholder="Your name"
          />
        </div>

        {/* Email */}
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-white/80 mb-2">
            Email
          </label>
          <input
            type="email"
            id="contact-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all"
            placeholder="your@email.com"
          />
        </div>

        {/* Inquiry Type */}
        <div>
          <label htmlFor="contact-inquiry" className="block text-sm font-medium text-white/80 mb-2">
            What can we help with?
          </label>
          <select
            id="contact-inquiry"
            value={inquiryType}
            onChange={(e) => setInquiryType(e.target.value as InquiryType)}
            required
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all appearance-none cursor-pointer"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='rgba(255,255,255,0.5)'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
          >
            <option value="" disabled className="bg-gray-900">Select an option...</option>
            {inquiryOptions.map((option) => (
              <option key={option.value} value={option.value} className="bg-gray-900">
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Message */}
        <div>
          <label htmlFor="contact-message" className="block text-sm font-medium text-white/80 mb-2">
            Message
          </label>
          <textarea
            id="contact-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            rows={4}
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-amber-500/50 focus:outline-none focus:ring-1 focus:ring-amber-500/50 transition-all resize-none"
            placeholder="Tell us more about your needs..."
          />
        </div>

        {/* Error Message */}
        {status === 'error' && (
          <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 text-sm">
            {errorMessage}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={status === 'loading'}
          className="w-full py-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold text-lg shadow-lg shadow-amber-500/30 transition-all hover:shadow-amber-500/50 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'loading' ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Sending...
            </span>
          ) : (
            'Send Message'
          )}
        </button>
      </div>
    </form>
  );
};

// Decorative Vegas lights component
const VegasLights: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Top marquee lights */}
    <div className="absolute top-0 left-0 right-0 h-1 flex justify-around">
      {Array.from({ length: 30 }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full bg-amber-400 bulb-${(i % 8) + 1}`}
          style={{ filter: 'blur(1px)' }}
        />
      ))}
    </div>
    {/* Ambient glow spots */}
    <div className="absolute top-20 left-10 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl spotlight" />
    <div className="absolute top-40 right-20 w-80 h-80 bg-red-500/10 rounded-full blur-3xl spotlight" style={{ animationDelay: '2s' }} />
    <div className="absolute bottom-40 left-1/3 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl spotlight" style={{ animationDelay: '4s' }} />
  </div>
);

// Floating card decorations
const FloatingCards: React.FC = () => (
  <div className="absolute inset-0 overflow-hidden pointer-events-none">
    {/* Playing cards scattered around */}
    <div className="absolute top-24 left-[3%] text-7xl opacity-15 float-card" style={{ animationDelay: '0s', transform: 'rotate(-15deg)' }}>🂡</div>
    <div className="absolute top-40 right-[5%] text-6xl opacity-12 float-card" style={{ animationDelay: '0.5s', transform: 'rotate(10deg)' }}>🂾</div>
    <div className="absolute top-[35%] left-[8%] text-5xl opacity-10 float-card" style={{ animationDelay: '1s', transform: 'rotate(-8deg)' }}>🃋</div>
    <div className="absolute top-[45%] right-[10%] text-6xl opacity-15 float-card" style={{ animationDelay: '1.5s', transform: 'rotate(12deg)' }}>🂱</div>
    <div className="absolute bottom-[35%] left-[12%] text-5xl opacity-10 float-card" style={{ animationDelay: '2s', transform: 'rotate(5deg)' }}>🃁</div>
    <div className="absolute bottom-[25%] right-[8%] text-7xl opacity-12 float-card" style={{ animationDelay: '2.5s', transform: 'rotate(-10deg)' }}>🃑</div>
    <div className="absolute top-[20%] left-[25%] text-4xl opacity-8 float-card" style={{ animationDelay: '3s', transform: 'rotate(20deg)' }}>🂢</div>
    <div className="absolute top-[60%] right-[25%] text-5xl opacity-10 float-card" style={{ animationDelay: '3.5s', transform: 'rotate(-5deg)' }}>🃎</div>
    
    {/* Poker chips */}
    <div className="absolute top-28 left-[15%] w-12 h-12 rounded-full bg-gradient-to-br from-red-600 to-red-800 border-4 border-red-400 opacity-20 float-card shadow-lg" style={{ animationDelay: '0.3s' }}>
      <div className="absolute inset-2 rounded-full border-2 border-dashed border-white/30"></div>
    </div>
    <div className="absolute top-[30%] right-[15%] w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 border-4 border-amber-300 opacity-25 float-card shadow-lg" style={{ animationDelay: '0.8s' }}>
      <div className="absolute inset-2 rounded-full border-2 border-dashed border-white/30"></div>
    </div>
    <div className="absolute top-[55%] left-[5%] w-10 h-10 rounded-full bg-gradient-to-br from-emerald-600 to-emerald-800 border-4 border-emerald-400 opacity-20 float-card shadow-lg" style={{ animationDelay: '1.3s' }}>
      <div className="absolute inset-1.5 rounded-full border-2 border-dashed border-white/30"></div>
    </div>
    <div className="absolute bottom-[40%] right-[3%] w-16 h-16 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 border-4 border-purple-400 opacity-20 float-card shadow-lg" style={{ animationDelay: '1.8s' }}>
      <div className="absolute inset-3 rounded-full border-2 border-dashed border-white/30"></div>
    </div>
    <div className="absolute bottom-[20%] left-[20%] w-11 h-11 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 border-4 border-blue-400 opacity-18 float-card shadow-lg" style={{ animationDelay: '2.3s' }}>
      <div className="absolute inset-2 rounded-full border-2 border-dashed border-white/30"></div>
    </div>
    <div className="absolute top-[70%] right-[20%] w-9 h-9 rounded-full bg-gradient-to-br from-white to-gray-300 border-4 border-gray-100 opacity-15 float-card shadow-lg" style={{ animationDelay: '2.8s' }}>
      <div className="absolute inset-1.5 rounded-full border-2 border-dashed border-gray-400/30"></div>
    </div>
    <div className="absolute top-16 right-[30%] w-8 h-8 rounded-full bg-gradient-to-br from-black to-gray-800 border-4 border-gray-600 opacity-15 float-card shadow-lg" style={{ animationDelay: '3.3s' }}>
      <div className="absolute inset-1 rounded-full border border-dashed border-white/20"></div>
    </div>
    
    {/* Stacked chip effect */}
    <div className="absolute bottom-[45%] left-[30%] opacity-15 float-card" style={{ animationDelay: '1.1s' }}>
      <div className="relative">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-800 border-3 border-red-400 shadow-lg absolute bottom-2 left-1"></div>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-800 border-3 border-red-400 shadow-lg absolute bottom-1 left-0.5"></div>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-600 to-red-800 border-3 border-red-400 shadow-lg relative"></div>
      </div>
    </div>
    
    {/* Another stacked chips */}
    <div className="absolute top-[40%] right-[35%] opacity-12 float-card" style={{ animationDelay: '2.1s' }}>
      <div className="relative">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 border-2 border-amber-300 shadow-lg absolute bottom-1.5 left-0.5"></div>
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 border-2 border-amber-300 shadow-lg relative"></div>
      </div>
    </div>
  </div>
);

// Neon sign component
const NeonSign: React.FC<{ children: React.ReactNode; color?: 'gold' | 'red' }> = ({ children, color = 'gold' }) => (
  <span className={color === 'gold' ? 'neon-gold' : 'neon-red'}>
    {children}
  </span>
);

export const LandingPage: React.FC<Props> = ({ onEnterApp, onViewPartners }) => {
  const { user, profile, roles, logout, loading } = useAuth();
  const [showAuthPanel, setShowAuthPanel] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Invite link params from URL
  const [inviteToken, setInviteToken] = useState<string | undefined>();
  const [inviteEmail, setInviteEmail] = useState<string | undefined>();

  const isAdmin = roles.includes('admin');
  const isDev = roles.includes('dev');
  const canAccessAdmin = isAdmin || isDev;

  // Check for invite params on mount
  useEffect(() => {
    setIsVisible(true);
    
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const email = params.get('email');
    
    if (token) {
      setInviteToken(token);
      setInviteEmail(email || undefined);
      setShowAuthPanel(true);
      // Clean up URL without reload
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const displayEmail = profile?.email || user?.email || 'Guest';

  const navLinks = [
    { href: '#demo', label: 'Try Demo' },
    { href: '#features', label: 'Features' },
    ...(BILLING_ENABLED ? [{ href: '#pricing', label: 'Pricing' }] : []),
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <div className="min-h-screen vegas-gradient text-white overflow-x-hidden">
      <VegasLights />
      <FloatingCards />

      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/70 border-b border-amber-500/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="flex items-center gap-3 shrink-0"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-300 via-amber-500 to-amber-600 flex items-center justify-center text-black font-extrabold text-lg font-cinzel shadow-md shadow-amber-500/30">
              JK
            </div>
            <div className="leading-none">
              <div className="font-cinzel font-bold text-lg tracking-wide gold-shimmer">JK CASINO</div>
              <div className="text-[10px] text-amber-200/50 tracking-[0.2em] uppercase mt-0.5">Blackjack Trainer</div>
            </div>
          </a>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7 text-sm">
            {navLinks.map((l) => (
              <a key={l.href} href={l.href} className="text-white/65 hover:text-amber-300 transition-colors">
                {l.label}
              </a>
            ))}
            <button
              onClick={onViewPartners}
              className="inline-flex items-center gap-1.5 text-white/65 hover:text-amber-300 transition-colors"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80"></span>
              For Teams
            </button>
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <>
                <div className="text-right hidden lg:block">
                  <div className="text-[10px] uppercase tracking-widest text-amber-400/50">Signed in as</div>
                  <div className="font-medium text-xs truncate max-w-[150px] text-white/80">{displayEmail}</div>
                </div>
                {canAccessAdmin && (
                  <button
                    onClick={() => setShowAdminPanel(true)}
                    className="hidden sm:inline px-3.5 py-2 rounded-lg bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-200 text-sm font-medium transition-all"
                  >
                    Admin
                  </button>
                )}
                <button
                  onClick={() => logout()}
                  disabled={loading}
                  className="hidden sm:inline px-3.5 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-medium transition-all disabled:opacity-50"
                >
                  Sign out
                </button>
                <button
                  onClick={onEnterApp}
                  className="px-4 sm:px-5 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold text-sm shadow-lg shadow-amber-500/25 transition-all hover:shadow-amber-500/40"
                >
                  Open Trainer
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setShowAuthPanel(true)}
                  className="hidden sm:inline px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-medium transition-all"
                >
                  Sign in
                </button>
                <button
                  onClick={() => setShowAuthPanel(true)}
                  className="px-4 sm:px-5 py-2 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-bold text-sm shadow-lg shadow-amber-500/25 transition-all hover:shadow-amber-500/40"
                >
                  Get Started
                </button>
              </>
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileMenuOpen(v => !v)}
              className="md:hidden p-2 -mr-1 rounded-lg text-white/80 hover:bg-white/10 transition-colors"
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>

        {/* Mobile dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-amber-500/15 bg-black/95 backdrop-blur-xl">
            <nav className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-3 py-2.5 rounded-lg text-white/75 hover:text-amber-300 hover:bg-white/5 transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <button
                onClick={() => { setMobileMenuOpen(false); onViewPartners(); }}
                className="flex items-center gap-2 text-left px-3 py-2.5 rounded-lg text-white/75 hover:text-amber-300 hover:bg-white/5 transition-colors"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400/80"></span>
                For Teams
              </button>

              <div className="h-px bg-white/10 my-2"></div>

              {user ? (
                <>
                  {canAccessAdmin && (
                    <button
                      onClick={() => { setMobileMenuOpen(false); setShowAdminPanel(true); }}
                      className="text-left px-3 py-2.5 rounded-lg text-purple-200 hover:bg-white/5 transition-colors"
                    >
                      Admin
                    </button>
                  )}
                  <button
                    onClick={() => { setMobileMenuOpen(false); onEnterApp(); }}
                    className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-sm text-center"
                  >
                    Open Trainer
                  </button>
                  <button
                    onClick={() => { setMobileMenuOpen(false); logout(); }}
                    className="text-left px-3 py-2.5 rounded-lg text-white/70 hover:bg-white/5 transition-colors"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => { setMobileMenuOpen(false); setShowAuthPanel(true); }}
                    className="text-left px-3 py-2.5 rounded-lg text-white/80 hover:bg-white/5 transition-colors"
                  >
                    Sign in
                  </button>
                  <button
                    onClick={() => { setMobileMenuOpen(false); setShowAuthPanel(true); }}
                    className="px-4 py-2.5 rounded-lg bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold text-sm text-center"
                  >
                    Get Started
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </header>

      <main>
        {/* Hero Section */}
        <section className="relative pt-16 pb-24 px-4 sm:px-6">
          <div className="max-w-7xl mx-auto">
            <div className={`text-center space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              {/* Vegas-style badge */}
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-gradient-to-r from-amber-500/20 via-amber-400/10 to-amber-500/20 border border-amber-500/30 backdrop-blur">
                <span className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-400 bulb-1"></span>
                  <span className="w-2 h-2 rounded-full bg-amber-400 bulb-2"></span>
                  <span className="w-2 h-2 rounded-full bg-amber-400 bulb-3"></span>
                </span>
                <span className="text-sm font-medium text-amber-200 tracking-wide">THE HOUSE EDGE ENDS HERE</span>
                <span className="flex gap-1">
                  <span className="w-2 h-2 rounded-full bg-amber-400 bulb-4"></span>
                  <span className="w-2 h-2 rounded-full bg-amber-400 bulb-5"></span>
                  <span className="w-2 h-2 rounded-full bg-amber-400 bulb-6"></span>
                </span>
              </div>

              {/* Main headline */}
              <h1 className="font-cinzel text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold leading-tight">
                <span className="block text-white">BECOME THE</span>
                <span className="block gold-shimmer">ADVANTAGE</span>
              </h1>

              <p className="text-xl sm:text-2xl text-white/70 max-w-3xl mx-auto leading-relaxed font-light">
                The most realistic blackjack training system ever built. 
                <span className="text-amber-400"> Master card counting</span>, 
                <span className="text-red-400"> perfect basic strategy</span>, and 
                <span className="text-amber-400"> crush the house edge</span>.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <a
                  href="#demo"
                  className="group relative px-10 py-4 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-black font-bold text-lg shadow-2xl shadow-amber-500/40 transition-all hover:shadow-amber-400/60 hover:scale-105 glow-pulse overflow-hidden text-center"
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    <span>Try It Now — Free</span>
                    <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </span>
                </a>
                <a
                  href="#features"
                  className="px-10 py-4 rounded-xl border-2 border-white/20 hover:border-amber-500/50 text-white font-semibold text-lg transition-all hover:bg-white/5"
                >
                  See Features
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-24 px-4 sm:px-6 relative">
          <div className="max-w-6xl mx-auto relative">
            {/* Section Header */}
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-red-500/10 border border-red-500/20 text-red-400 text-sm font-medium mb-4">
                <span>♠</span> FULL SUITE <span>♥</span>
              </div>
              <h2 className="font-cinzel text-4xl sm:text-5xl font-bold mb-4">
                <NeonSign>Everything You Need</NeonSign>
              </h2>
              <p className="text-xl text-white/60 max-w-2xl mx-auto">
                A serious training system for players who want to beat the house.
              </p>
            </div>

            {/* Hero Features - 3 main selling points */}
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              {heroFeatures.map((feature, idx) => (
                <div
                  key={feature.title}
                  className={`relative p-8 rounded-2xl border transition-all duration-300 hover:scale-[1.02] ${
                    idx === 1 
                      ? 'bg-gradient-to-br from-amber-500/20 via-amber-600/10 to-transparent border-amber-500/40 shadow-lg shadow-amber-500/10' 
                      : 'bg-gradient-to-br from-white/[0.08] to-white/[0.02] border-white/10 hover:border-amber-500/30'
                  }`}
                >
                  <div className="text-5xl mb-4">{feature.icon}</div>
                  <h3 className="font-cinzel text-2xl font-bold text-white mb-1">{feature.title}</h3>
                  <div className="text-amber-400/80 text-sm font-medium mb-3">{feature.subtitle}</div>
                  <p className="text-white/60 leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Feature Categories */}
            <div className="grid md:grid-cols-2 gap-8">
              {featureCategories.map((category) => (
                <div key={category.title} className="p-6 rounded-2xl bg-white/[0.03] border border-white/5">
                  <h4 className="font-cinzel text-lg font-bold text-amber-400 mb-4 flex items-center gap-2">
                    <span className="w-8 h-px bg-gradient-to-r from-amber-500/50 to-transparent" />
                    {category.title}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {category.items.map((item) => (
                      <div key={item.name} className="flex items-start gap-3 p-3 rounded-lg hover:bg-white/[0.03] transition-colors">
                        <span className="text-2xl flex-shrink-0">{item.icon}</span>
                        <div>
                          <div className="font-semibold text-white text-sm">{item.name}</div>
                          <div className="text-white/50 text-xs">{item.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section (only shown once billing is live) */}
        {BILLING_ENABLED && (
          <section id="pricing" className="py-24 px-4 sm:px-6 relative">
            <div className="max-w-6xl mx-auto relative">
              <div className="text-center mb-12">
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
                  <span>♣</span> SIMPLE PRICING <span>♦</span>
                </div>
                <h2 className="font-cinzel text-4xl sm:text-5xl font-bold mb-4">
                  <NeonSign>Train Like a Pro</NeonSign>
                </h2>
                <p className="text-xl text-white/60 max-w-2xl mx-auto">
                  Start free with the hand trainer. Upgrade for the full suite.
                </p>
              </div>
              {/* On the landing page the visitor isn't signed in yet, so the CTA
                  routes them into the app to sign in / sign up, then upgrade. */}
              <PricingSection onSelect={() => onEnterApp()} />

              <div className="text-center mt-8">
                <button onClick={onViewPartners} className="text-amber-300 hover:text-amber-400 underline underline-offset-4 font-medium">
                  Running a team, cohort, or audience? See partner plans →
                </button>
              </div>
            </div>
          </section>
        )}

        {/* Interactive Demo Section */}
        <section id="demo" className="py-24 px-4 sm:px-6 relative overflow-hidden">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-sm font-medium mb-4">
                ♣ TRY IT NOW ♦
              </div>
              <h2 className="font-cinzel text-4xl sm:text-5xl font-bold mb-4">
                Feel the <span className="text-emerald-400">Felt</span>
              </h2>
              <p className="text-xl text-white/60 max-w-2xl mx-auto">
                Play a few hands right here. No signup required—just place your bet and deal.
              </p>
            </div>
            
            {/* Interactive Demo */}
            <div className="relative max-w-4xl mx-auto">
              <DemoHandTrainer onSignUpClick={() => setShowAuthPanel(true)} />
            </div>

            {/* Feature highlights below demo */}
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
              {['Authentic Vegas rules', 'Multiple counting systems', 'Real-time deviation alerts', 'Bankroll simulation'].map((item) => (
                <div key={item} className="flex items-center gap-3 text-white/80 bg-white/5 rounded-lg px-4 py-3">
                  <span className="w-6 h-6 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 text-sm flex-shrink-0">✓</span>
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-24 px-4 sm:px-6 relative">
          <div className="max-w-2xl mx-auto relative">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-sm font-medium mb-4">
                GET IN TOUCH
              </div>
              <h2 className="font-cinzel text-4xl sm:text-5xl font-bold mb-4">
                Let's <span className="gold-shimmer">Talk</span>
              </h2>
              <p className="text-xl text-white/60 max-w-xl mx-auto">
                Free trial, partnership, custom or profit-share terms, or a pricing question? Send a message.
              </p>
            </div>

            {/* Custom & Partnerships — always available (no billing needed), so deals
                can start before self-serve pricing goes live. */}
            <div className="mb-6 p-6 rounded-2xl bg-gradient-to-br from-amber-500/15 to-transparent border border-amber-500/30">
              <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div>
                  <h3 className="font-cinzel text-xl font-bold text-white flex items-center gap-2">
                    <span aria-hidden="true">🤝</span> Custom &amp; Partnerships
                  </h3>
                  <p className="text-white/60 text-sm mt-1 max-w-md">
                    Revenue-share, profit-sharing, or bespoke terms for creators, coaches, and teams.
                    Let&rsquo;s build something that works for both of us.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => openContactInquiry('custom')}
                  className="shrink-0 px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-gray-900 font-bold text-sm transition-colors"
                >
                  Let&rsquo;s talk
                </button>
              </div>
            </div>

            <ContactForm />
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-24 px-4 sm:px-6 relative overflow-hidden">
          {/* Vegas marquee border effect */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-4 border border-amber-500/30 rounded-3xl">
              <div className="absolute top-0 left-0 right-0 h-px flex justify-around">
                {Array.from({ length: 40 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1.5 h-1.5 -mt-0.5 rounded-full bg-amber-400 bulb-${(i % 8) + 1}`}
                  />
                ))}
              </div>
              <div className="absolute bottom-0 left-0 right-0 h-px flex justify-around">
                {Array.from({ length: 40 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1.5 h-1.5 -mb-0.5 rounded-full bg-amber-400 bulb-${((i + 4) % 8) + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto text-center relative">
            <h2 className="font-cinzel text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              The Cards Are <span className="neon-gold neon-flicker">Waiting</span>
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto mb-10">
              Every hand you don't practice is money left on the table. 
              Ready to take the edge back from the house?
            </p>
            <div className="flex justify-center">
              <a
                href="#contact"
                className="px-10 py-4 rounded-xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 text-black font-bold text-lg shadow-2xl shadow-amber-500/40 transition-all hover:shadow-amber-400/60 hover:scale-105 glow-pulse"
              >
                Get Started
              </a>
            </div>
            
            {/* Card suits decoration */}
            <div className="mt-12 flex justify-center gap-4 text-4xl opacity-30">
              <span>♠</span>
              <span className="text-red-400">♥</span>
              <span className="text-red-400">♦</span>
              <span>♣</span>
            </div>
          </div>
        </section>

        {/* Auth Panel Modal */}
        {showAuthPanel && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="relative w-full max-w-lg">
                <button
                  onClick={() => setShowAuthPanel(false)}
                className="absolute -top-12 right-0 text-white/60 hover:text-white transition-colors"
                >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                </button>
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl border border-white/10 p-6">
                <h3 className="font-cinzel text-2xl font-bold text-center mb-6">
                  <span className="gold-shimmer">Join the Table</span>
                </h3>
                <AuthPanel
                  initialToken={inviteToken}
                  initialEmail={inviteEmail}
                  onClose={() => {
                    setShowAuthPanel(false);
                    setInviteToken(undefined);
                    setInviteEmail(undefined);
                  }}
                  onAuthenticated={() => {
                    setShowAuthPanel(false);
                    setInviteToken(undefined);
                    setInviteEmail(undefined);
                    onEnterApp();
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center text-black font-bold font-cinzel">
                JK
              </div>
              <div>
                <div className="font-cinzel font-bold text-lg gold-shimmer">JK CASINO</div>
                <div className="text-xs text-white/40">Blackjack Trainer</div>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-white/50">
              <a href="/terms.html" className="hover:text-white transition-colors">Terms</a>
              <a href="/privacy.html" className="hover:text-white transition-colors">Privacy</a>
              <a href="/disclaimer.html" className="hover:text-white transition-colors">Disclaimer</a>
            </div>
            <div className="text-sm text-white/40 text-center md:text-right">
              <div className="mt-1 flex items-center gap-2 justify-center md:justify-end">
                <span>Built by</span>
                <a 
                  href="https://jacobkanfer.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 text-amber-400/70 hover:text-amber-400 transition-colors"
                >
                  <img src="/jk-logo.svg" alt="JK" className="w-5 h-5" />
                  <span>Jacob Kanfer</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Admin Panel Modal */}
      <AdminPanelModal isOpen={showAdminPanel} onClose={() => setShowAdminPanel(false)} />
    </div>
  );
};

export default LandingPage;
